import { useCallback, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Breadcrumbs, Container, Grid, Link, Typography, Button } from '@material-ui/core';
import { formationApi } from '../../../api/formationApi';
import FormationsFilterSatisfactionPerformance from '../../../components/dashboard/formation/FormationsFilterSatisfactionPerformance';
import FormationsResultsSatisfaction from '../../../components/dashboard/formation/FormationsResultsSatisfaction';
import useMounted from '../../../hooks/useMounted';
import ChevronRightIcon from '../../../icons/ChevronRight';
import gtm from '../../../lib/gtm';
import useAuth from '../../../hooks/useAuth';
import ArrowLeftIcon from '../../../icons/ArrowLeft';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, lightVeil, grey } from '../../../theme/tones';

const AnimatedMeshBackground = () => (
    <Box sx={{ 
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
        overflow: 'hidden', zIndex: 0, opacity: 0.4,
        background: (theme) => `${theme.palette.background.default}`,
        pointerEvents: 'none'
    }}>
        <motion.div
            animate={{ x: [0, 120, -60, 0], y: [0, -120, 60, 0], scale: [1, 1.3, 0.8, 1] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', top: '-15%', left: '-10%', width: '45vw', height: '45vw', background: `radial-gradient(circle, ${toneAlpha('indigo', 0.12)} 0%, ${lightVeil(0)} 70%)`, borderRadius: '50%', filter: 'blur(60px)' }}
        />
        <motion.div
            animate={{ x: [0, -100, 80, 0], y: [0, 100, -50, 0], scale: [1, 1.2, 0.9, 1] }}
            transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', bottom: '-25%', right: '-10%', width: '55vw', height: '55vw', background: `radial-gradient(circle, ${toneAlpha('emerald', 0.1)} 0%, ${lightVeil(0)} 70%)`, borderRadius: '50%', filter: 'blur(60px)' }}
        />
    </Box>
);

const FormationsSatisfaction = () => {
  const mounted = useMounted();
  const auth = useAuth();
  const [filters, setFilters] = useState([]);
  const [formations, setFormations] = useState([]);
  const [filteredFormations, setFilteredFormations] = useState([]);
  const [loading, setLoading] = useState(false);

  // remet filtres à zéro de src/components/dashboard/customer/CustomerListTable.js
  window.localStorage.setItem('choiceTabProfession', JSON.stringify('all'));
  window.localStorage.setItem('choiceSort', JSON.stringify('lastname|asc'));
  window.localStorage.setItem('choiceSource', JSON.stringify('all'));
  window.localStorage.setItem('choiceCampaign', JSON.stringify('all'));
  window.localStorage.setItem('choiceCallTrack', JSON.stringify('all'));
  window.localStorage.setItem('choiceCallTrackRelance', JSON.stringify('all'));
  window.localStorage.setItem('choiceAvancement', JSON.stringify('all'));

  useEffect(() => {
    gtm.push({ event: 'page_view' });
  }, []);

  const getFormationsSatisfactions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await formationApi.getFormationsForSatisfactions(localStorage.getItem('accessToken'), auth?.user?.id);
        if (mounted.current) {
          setFormations(data);
          setFilteredFormations(data);
          setLoading(false);
        }
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  }, [mounted]);


  // useEffect(() => {
  //   getFormations();
  // }, [getFormations]);

  
  useEffect(() => {
    getFormationsSatisfactions();
  }, [getFormationsSatisfactions]);

  useEffect(() => {
    if (filters.length > 0) {
      const tmpFormations = formations;
      // const tmpFilteredFormations = [];
      // for (let i = 0; i < tmpFormations.length; i++) {
      //   if (filters.includes(tmpFormations[i]?.year.toString())) {
      //     tmpFilteredFormations.push(tmpFormations[i]);
      //   }
      // }
      const tmpFilteredFormations = tmpFormations.filter((f) => filters.includes(f?.year?.toString()));
      setFilteredFormations(tmpFilteredFormations);
    } else {
      setFilteredFormations(formations);
    }
  }, [filters]);

  return (
    <>
      <Helmet>
        <title>Satisfactions | Syndays</title>
      </Helmet>
      <Box
        sx={{
          bgcolor: 'action.hover',
          minHeight: '100%',
          position: 'relative',
          overflow: 'hidden',
          pb: 10,
          pt: 4
        }}
      >
        <AnimatedMeshBackground />

        <Container maxWidth={false} disableGutters sx={{ px: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/dashboard"
              startIcon={<ArrowLeftIcon style={{ fontSize: 14 }} />}
              size="small"
              sx={{
                color: 'text.secondary',
                fontWeight: 800,
                textTransform: 'none',
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.03),
                borderRadius: '10px',
                px: 2,
                '&:hover': { bgcolor: (theme) => alpha(theme.palette.common.black, 0.06) }
              }}
            >
              Retour
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Box sx={{ 
                  p: 3, borderRadius: '24px', 
                  background: (theme) => alpha(theme.palette.common.white, 0.8), backdropFilter: 'blur(10px)', border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
                  boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.common.black, 0.03)}`,
                  display: 'flex', alignItems: 'center', gap: 3
              }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.tones.indigo.base} 0%, ${theme.palette.tones.indigo.color} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.tones.indigo.base, 0.3)}`,
                    flexShrink: 0,
                  }}
                >
                  <SentimentSatisfiedAltIcon sx={{ color: grey(0), fontSize: '1.7rem' }} />
                </Box>
                <Box>
                  <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 1 }}>
                      <Link color="textPrimary" component={RouterLink} to="/dashboard" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', '&:hover': { color: getTone('indigo').base } }}>Dashboard</Link>
                      <Link color="textPrimary" component={RouterLink} to="/dashboard/satisfactions" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', '&:hover': { color: getTone('indigo').base } }}>Satisfactions</Link>
                      <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Liste</Typography>
                  </Breadcrumbs>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em', mb: 0.5 }}>
                      Liste des satisfactions par formation
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Consultez et analysez les retours de satisfaction pour chacune de vos formations.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ my: 3 }}>
            <FormationsFilterSatisfactionPerformance setFilters={setFilters} />
          </Box>
          <FormationsResultsSatisfaction
            formations={filteredFormations}
            setFormations={setFormations}
            filters={filters}
            loading={loading}
          />
        </Container>
      </Box>
    </>
  );
};

export default FormationsSatisfaction;
